import { CSSProperties, useState } from "react";
import "keen-slider/keen-slider.min.css";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import {
  KeenSliderHooks,
  KeenSliderInstance,
  useKeenSlider,
} from "keen-slider/react";
import classNames from "classnames";

export type SliderType = KeenSliderInstance<{}, {}, KeenSliderHooks>;
interface Props {
  initialSlide?: number;
  onSlideChange?: (slider: SliderType) => void;
  slides: JSX.Element[];
  style?: CSSProperties;
  loop?: boolean;
  perView?: number | "auto" | (() => number | "auto");
  spacing?: number;
  type?: "dots" | "arrows" | "autoSwitch" | "";
}
const Slider: React.FC<Props> = ({
  slides,
  initialSlide = 0,
  onSlideChange,
  loop,
  perView = 1,
  spacing = 0,
  type = "dots",
  style,
}) => {
  const [currentPosition, setCurrentPosition] = useState(initialSlide);
  const [refCallback, slider] = useKeenSlider(
    {
      initial: initialSlide,
      loop: loop,

      slides: {
        perView: perView,
        spacing: spacing,
      },
      slideChanged(slider) {
        setCurrentPosition(slider.track.details.rel);
        onSlideChange?.(slider);
      },
    },
    [
      (slider) => {
        if (type !== "autoSwitch") return;
        const DELAY_TIME = 3000;
        let timeout: any;
        let mouseOver = false;
        function clearNextTimeout() {
          clearTimeout(timeout);
        }
        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => {
            slider.next();
          }, DELAY_TIME);
        }
        slider.on("created", () => {
          slider.container.addEventListener("mouseover", () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener("mouseout", () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });
        slider.on("dragStarted", clearNextTimeout);
        slider.on("animationEnded", nextTimeout);
        slider.on("updated", nextTimeout);
      },
    ]
  );

  if (type === "arrows") {
    const details = slider?.current?.track.details;
    // @ts-ignore
    const showLeftArrow = details?.abs > 0;
    // @ts-ignore
    const showRightArrow = details?.abs < details?.maxIdx;

    return (
      <div className={classNames("relative w-full")}>
        {showLeftArrow && (
          <div
            className="h-full md:h-[412px] w-6 absolute z-20 left-0 cursor-pointer"
            onClick={() => slider.current?.prev()}
          >
            <ChevronLeftIcon
              className="h-6 w-6 text-gray-new absolute top-[50%] z-30 left-0"
              onClick={() => slider.current?.prev()}
            />
          </div>
        )}
        {showRightArrow && (
          <div
            className="h-full md:h-[412px] w-6 absolute z-20 right-0 cursor-pointer"
            onClick={() => slider.current?.next()}
          >
            <ChevronRightIcon
              className="h-6 w-6 text-gray-new absolute top-[50%] z-30 right-0"
              onClick={() => slider.current?.next()}
            />
          </div>
        )}
        <div ref={refCallback} className={"keen-slider "} style={style}>
          {slides}
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={refCallback} className={"keen-slider "} style={style}>
        {slides}
      </div>
      {type === "dots" &&  slides.length > 1 && (
        <div className="flex w-full justify-center pt-1 gap-1">
          {slides.map((_slide, index) => (
            <span
              key={index}
              onClick={() => slider.current?.moveToIdx(index)}
              className={`h-1.5 w-1.5 rounded-full  ${
                index === currentPosition && perView == 1 ? "bg-gray-600/70" : "bg-gray-300/90"
              } `}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default Slider;
